'use client'

import { useCallback } from 'react'
import dynamic from 'next/dynamic'
import { ShoppingCart } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { useCart } from '@/context/cart-context'
import { useCartUI } from '@/context/cart-ui-context'
import { metaTrack } from '@/lib/meta-pixel'
import { formatPriceInteger } from '@/lib/utils'
import { useRouter } from 'next/navigation'
import { CartItem } from './cart-item'

const ShippingCalculator = dynamic(
  () =>
    import('@/components/shipping/shipping-calculator').then(mod => ({
      default: mod.ShippingCalculator,
    })),
  {
    ssr: false,
    loading: () => (
      <div className='flex items-center justify-center p-4'>
        <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900'></div>
        <span className='ml-2 text-sm text-gray-600'>
          Cargando calculadora...
        </span>
      </div>
    ),
  }
)
const FREE_SHIPPING_THRESHOLD = 100000

export default function CartDrawer() {
  const {
    items,
    removeItem,
    updateQuantity,
    total,
    totalWeight,
    itemCount,
    deliveryMethod,
    selectedShipping,
    setSelectedShipping,
  } = useCart()

  const { isCartOpen, closeCart } = useCartUI()
  const router = useRouter()
  const amountToFreeShipping = Math.max(0, FREE_SHIPPING_THRESHOLD - total)
  const hasFreeShipping =
    deliveryMethod === 'shipping' && total >= FREE_SHIPPING_THRESHOLD
  const effectiveShippingPrice = hasFreeShipping
    ? 0
    : (selectedShipping?.price ?? 0)

  const effectiveTotal = total + effectiveShippingPrice
  const progress = Math.min(100, (total / FREE_SHIPPING_THRESHOLD) * 100)

  const handleUpdateQuantity = useCallback(
    (productId: string, quantity: number) =>
      updateQuantity(productId, quantity),
    [updateQuantity]
  )

  const handleRemoveItem = useCallback(
    (productId: string) => removeItem(productId),
    [removeItem]
  )

  const handleCheckout = useCallback(() => {
    metaTrack('InitiateCheckout', {
      value: effectiveTotal,
      currency: 'ARS',
      num_items: itemCount,
      contents: items.map(item => ({
        id: item.id,
        quantity: item.quantity,
        item_price: item.price,
      })),
    })
    if (typeof window !== 'undefined' && (window as any).gtag) {
      ;(window as any).gtag('event', 'begin_checkout', {
        currency: 'ARS',
        value: effectiveTotal,
        items: items.map(item => ({
          item_id: item.id,
          item_name: item.name,
          price: item.price,
          quantity: item.quantity,
        })),
      })
    }
    closeCart()
    router.push('/checkout')
  }, [closeCart, router, items, effectiveTotal, itemCount])

  return (
    <Sheet
      open={isCartOpen}
      onOpenChange={open => {
        if (!open) closeCart()
      }}
    >
      <SheetContent
        side='right'
        className='w-full sm:w-[480px] max-h-screen overflow-y-auto'
        onOpenAutoFocus={event => event.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle className='flex items-center gap-2'>
            <ShoppingCart className='h-5 w-5' />
            Tu Carrito ({itemCount})
          </SheetTitle>
        </SheetHeader>

        <div className='mt-6 space-y-4'>
          {items.length === 0 ? (
            <div className='text-center py-8'>
              <ShoppingCart className='h-12 w-12 mx-auto mb-4 text-gray-400' />
              <p className='text-gray-500'>Tu carrito está vacío</p>
            </div>
          ) : (
            <>
              {items.map(item => (
                <CartItem
                  key={item.id}
                  item={item}
                  onRemove={handleRemoveItem}
                  onUpdateQuantity={handleUpdateQuantity}
                />
              ))}

              <div className='pt-2 space-y-4'>
                {deliveryMethod === 'shipping' && (
                  <div className='px-1 pt-1 pb-1'>
                    <p className='text-[13px] leading-[1.35] text-[#8A8165]'>
                      {hasFreeShipping ? (
                        <>🫶 Tenés envío gratis</>
                      ) : (
                        <>
                          Te faltan{' '}
                          <span className='font-medium text-[#8A8165]'>
                            {formatPriceInteger(amountToFreeShipping)}
                          </span>{' '}
                          para envío gratis
                        </>
                      )}
                    </p>

                    {!hasFreeShipping && (
                      <div className='mt-2 h-[4px] w-full rounded-full bg-[#E7E0D2]/80 overflow-hidden'>
                        <div
                          className='h-full rounded-full bg-[#8A8165] transition-all duration-500'
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}

                {deliveryMethod === 'shipping' && !hasFreeShipping && (
                  <div className='bg-gray-50 p-3 rounded-lg'>
                    <h4 className='text-sm font-normal text-gray-900 mb-3'>
                      Calcular Envío
                    </h4>

                    <ShippingCalculator
                      cartTotal={total}
                      cartWeight={totalWeight}
                      cartVolume={items.reduce(
                        (acc, item) =>
                          acc + (item.volumeCm3 ?? 0) * item.quantity,
                        0
                      )}
                      onShippingSelect={setSelectedShipping}
                      hasFreeShipping={hasFreeShipping}
                      compact
                    />
                  </div>
                )}

                <div className='!mt-7 space-y-2'>
                  <div className='flex justify-between text-sm'>
                    <span className='text-gray-600'>Subtotal:</span>
                    <span>{formatPriceInteger(total)}</span>
                  </div>

                  <div className='flex justify-between text-sm'>
                    <span className='text-gray-600'>Envío:</span>
                    <span
                      className={
                        hasFreeShipping ? 'text-green-600 font-medium' : ''
                      }
                    >
                      {hasFreeShipping
                        ? 'Gratis'
                        : selectedShipping
                          ? formatPriceInteger(selectedShipping.price)
                          : 'Calculalo para verlo'}
                    </span>
                  </div>

                  {(hasFreeShipping || selectedShipping) && (
                    <div className='border-t pt-2 flex justify-between items-center text-[16px] font-medium'>
                      <span>Total:</span>
                      <span className='text-[#8a7e55] tracking-[0.01em]'>
                        {formatPriceInteger(effectiveTotal)}
                      </span>
                    </div>
                  )}
                </div>

                <Button
                  variant='primary'
                  size='lg'
                  className='w-full font-allround font-semibold'
                  onClick={handleCheckout}
                  disabled={items.length === 0}
                >
                  Iniciar compra
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
